import React from 'react';
import {
  DatePicker,
  DatePickerChangeEvent,
  DatePickerProps,
} from '@progress/kendo-react-dateinputs';

export interface BaseDatePickerProps extends Omit<DatePickerProps, 'onChange'> {
  value?: Date | null;
  onChange?: (value: Date | null) => void;
  placeholder?: string;
  format?: string;
  min?: Date;
  max?: Date;
  disabled?: boolean;
  className?: string;
  label?: string;
  error?: string;
  required?: boolean;
  size?: 'small' | 'medium' | 'large';
}

const BaseDatePicker: React.FC<BaseDatePickerProps> = ({
  value,
  onChange,
  placeholder = 'Select a date',
  format = 'MM/dd/yyyy',
  min,
  max,
  disabled = false,
  className = '',
  label,
  error,
  required = false,
  ...otherProps
}) => {
  const [internalValue, setInternalValue] = React.useState<Date | null>(
    value ?? null,
  );

  // Sync when parent value changes externally
  React.useEffect(() => {
    setInternalValue(value ?? null);
  }, [value]);

  const handleChange = (event: DatePickerChangeEvent) => {
    const newValue = event.value;
    // Always update internal state so Kendo can manage segments and auto-advance
    setInternalValue(newValue);

    if (!onChange) return;

    // Calendar/popup selection — propagate immediately
    if (event.syntheticEvent?.type === 'click') {
      onChange(newValue);
      return;
    }

    // Keyboard input — only propagate when date is fully valid with 4-digit year
    if (
      newValue &&
      !Number.isNaN(newValue.getTime()) &&
      newValue.getFullYear() >= 1000
    ) {
      onChange(newValue);
    }
  };

  return (
    <div className={`tw-w-full ${className}`}>
      {label && (
        <div className="tw-flex tw-items-center tw-gap-1 tw-mb-1">
          <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700">
            {label}
          </label>
          {!required && (
            <span className="tw-text-xs tw-font-normal tw-text-unum-neutral-500">
              (optional)
            </span>
          )}
        </div>
      )}
      <DatePicker
        value={internalValue}
        onChange={handleChange}
        placeholder={placeholder}
        format={format}
        formatPlaceholder={{ year: 'yyyy', month: 'mm', day: 'dd' }}
        min={min}
        max={max}
        disabled={disabled}
        className={`tw-w-full ${error ? 'tw-border-red-500' : ''}`}
        {...otherProps}
      />
      {error && (
        <div className="tw-text-red-600 tw-text-sm tw-mt-1">{error}</div>
      )}
    </div>
  );
};

export default BaseDatePicker;
